/** Express Entry → structured profile for the Federal Skilled Worker assessment (D-079).
 * Every answer the rules need has its own field; an unknown answer stays empty (never guessed). */

import { type Dossier, useImmigrationDossiersPartialUpdate } from "@agencyos/api-client";
import { Button, GlassCard, SelectField, SoftToggle, TextField } from "@agencyos/ui-web";
import { useQueryClient } from "@tanstack/react-query";
import { type ReactNode, useState } from "react";
import { useTranslation } from "react-i18next";

import { fieldErrors } from "../../lib/settings";

export type Json = Record<string, unknown>;

const EDUCATION = ["less_than_secondary", "secondary", "one_year", "two_year", "bachelor", "two_or_more", "master", "doctoral"];
const TESTS = ["", "tcf_canada", "tef_canada", "ielts", "celpip", "pte"];
const ABILITIES = ["reading", "writing", "listening", "speaking"] as const;
const TEER = ["", "0", "1", "2", "3", "4", "5"];

function num(value: unknown): string {
  return typeof value === "number" ? String(value) : "";
}

function toNumber(text: string): number | undefined {
  const clean = text.trim().replace(",", ".");
  return /^\d+(\.\d+)?$/.test(clean) ? Number(clean) : undefined;
}

/** Yes / no / not known yet: "not known" is kept apart from "no". */
function TriSelect({ label, value, onChange }: { label: string; value: unknown; onChange: (v: boolean | undefined) => void }) {
  const { t } = useTranslation();
  return (
    <SelectField
      label={label}
      value={value === true ? "yes" : value === false ? "no" : ""}
      options={[
        { value: "", label: t("ee.unknown") },
        { value: "yes", label: t("common.yes") },
        { value: "no", label: t("common.no") },
      ]}
      onChange={(e) => onChange(e.target.value === "" ? undefined : e.target.value === "yes")}
    />
  );
}

function LanguageFields({ label, value, onChange }: { label: string; value: Json | undefined; onChange: (v: Json | undefined) => void }) {
  const { t } = useTranslation();
  const test = String(value?.test ?? "");
  const isTcf = test === "tcf_canada";
  const source = ((isTcf ? value?.raw_scores : value?.clb) ?? {}) as Record<string, number>;
  const set = (ability: string, text: string) => {
    const next = { ...source, [ability]: toNumber(text) ?? 0 };
    onChange({ ...(value ?? {}), test, ...(isTcf ? { raw_scores: next } : { clb: next }) });
  };
  return (
    <div className="stack">
      <div className="form-grid">
        <SelectField
          label={label}
          value={test}
          options={TESTS.map((x) => ({ value: x, label: t(`ee.tests.${x}`) }))}
          onChange={(e) => onChange(e.target.value ? { test: e.target.value } : undefined)}
        />
        {test ? (
          <TextField
            label={t("fsw.form.testDate")}
            type="date"
            value={String(value?.test_date ?? "")}
            onChange={(e) => onChange({ ...(value ?? {}), test, test_date: e.target.value || undefined })}
          />
        ) : null}
      </div>
      {test ? (
        <div className="form-grid form-grid--4">
          {ABILITIES.map((ability) => (
            <TextField
              key={ability}
              label={`${t(`ee.abilities.${ability}`)} (${isTcf ? "TCF" : t("ee.clb")})`}
              inputMode="numeric"
              value={num(source[ability])}
              onChange={(e) => set(ability, e.target.value)}
            />
          ))}
        </div>
      ) : null}
    </div>
  );
}

type Row = { employer?: string; noc?: string; from?: string; to?: string; hours_per_week?: number; paid?: boolean };

/** Periods of work (Schedule A: the officer reads a history without gaps). */
function WorkHistory({ rows, onChange }: { rows: Row[]; onChange: (rows: Row[]) => void }) {
  const { t } = useTranslation();
  const set = (index: number, patch: Row) => onChange(rows.map((row, i) => (i === index ? { ...row, ...patch } : row)));
  return (
    <div className="stack">
      <h4 className="muted">{t("fsw.form.history")}</h4>
      {rows.map((row, index) => (
        <div key={index} className="form-grid history-row">
          <TextField label={t("fsw.form.employer")} value={row.employer ?? ""} onChange={(e) => set(index, { employer: e.target.value })} />
          <TextField label={t("fsw.form.noc")} inputMode="numeric" maxLength={5} value={row.noc ?? ""} onChange={(e) => set(index, { noc: e.target.value.replace(/\D/g, "") })} />
          <TextField label={t("fsw.form.from")} type="date" value={row.from ?? ""} onChange={(e) => set(index, { from: e.target.value })} />
          <TextField label={t("fsw.form.to")} type="date" hint={t("fsw.form.toHint")} value={row.to ?? ""} onChange={(e) => set(index, { to: e.target.value })} />
          <TextField label={t("fsw.form.hours")} inputMode="numeric" value={num(row.hours_per_week)} onChange={(e) => set(index, { hours_per_week: toNumber(e.target.value) })} />
          <TriSelect label={t("fsw.form.paid")} value={row.paid} onChange={(v) => set(index, { paid: v })} />
          <Button variant="ghost" onClick={() => onChange(rows.filter((_, i) => i !== index))}>
            {t("fsw.form.removeRow")}
          </Button>
        </div>
      ))}
      <div className="row">
        <Button variant="secondary" onClick={() => onChange([...rows, { paid: true }])}>
          {t("fsw.form.addRow")}
        </Button>
      </div>
    </div>
  );
}

function Section({ title, hint, children }: { title: string; hint?: string; children: ReactNode }) {
  return (
    <section className="form-section">
      <div className="form-section__head">
        <h3>{title}</h3>
        {hint ? <p className="muted">{hint}</p> : null}
      </div>
      <div className="stack">{children}</div>
    </section>
  );
}

export function ProfileForm({ dossier }: { dossier: Dossier }) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const refresh = () =>
    void queryClient.invalidateQueries({ predicate: (q) => String(q.queryKey[0] ?? "").startsWith("/api/v1/immigration/") });
  const update = useImmigrationDossiersPartialUpdate({ mutation: { onSuccess: refresh } });
  const [profile, setProfile] = useState<Json>(() => (dossier.profile as Json) ?? {});
  const principal = (profile.principal ?? {}) as Json;
  const spouse = (profile.spouse ?? {}) as Json;
  const additional = (profile.additional ?? {}) as Json;
  const skilled = (principal.skilled_experience ?? {}) as Json;
  const spouseSkilled = (spouse.skilled_experience ?? {}) as Json;
  const eca = (principal.eca ?? {}) as Json;
  const milestones = (profile.milestones ?? {}) as Json;
  const setRoot = (patch: Json) => setProfile((p) => ({ ...p, ...patch }));
  const setP = (patch: Json) => setProfile((p) => ({ ...p, principal: { ...((p.principal as Json) ?? {}), ...patch } }));
  const setS = (patch: Json) => setProfile((p) => ({ ...p, spouse: { ...((p.spouse as Json) ?? {}), ...patch } }));
  const setA = (patch: Json) => setProfile((p) => ({ ...p, additional: { ...((p.additional as Json) ?? {}), ...patch } }));
  const setSkilled = (patch: Json) => setP({ skilled_experience: { ...skilled, ...patch } });
  const setEca = (patch: Json) => setP({ eca: { ...eca, ...patch } });
  const setM = (patch: Json) => setRoot({ milestones: { ...milestones, ...patch } });
  const errors = fieldErrors(update.error);

  const save = () => {
    // Funds exemption for the checklist follows the same rule as the assessment.
    const exempt = profile.job_offer === true && profile.authorized_to_work_canada === true;
    update.mutate({ id: dossier.id, data: { profile: { ...profile, funds_exempt: exempt } } });
  };

  return (
    <GlassCard as="section" className="stack">
      <div className="section-title">
        <div>
          <h2>{t("ee.profile")}</h2>
          <p className="muted">{t("fsw.form.intro")}</p>
        </div>
      </div>

      <Section title={t("fsw.form.identity")}>
        <div className="form-grid">
          <TextField label={t("ee.age")} inputMode="numeric" value={num(principal.age)} onChange={(e) => setP({ age: toNumber(e.target.value) })} />
          <TextField label={t("fsw.form.birth")} type="date" value={String(principal.date_of_birth ?? "")} onChange={(e) => setP({ date_of_birth: e.target.value || undefined })} />
          <TextField label={t("ee.passportName")} value={String(principal.passport_name ?? "")} onChange={(e) => setP({ passport_name: e.target.value })} />
          <SelectField
            label={t("ee.maritalStatus")}
            value={String(profile.marital_status ?? "single")}
            options={["single", "married", "common_law", "divorced", "widowed"].map((x) => ({ value: x, label: t(`ee.marital.${x}`) }))}
            onChange={(e) => setRoot({ marital_status: e.target.value })}
          />
          <TextField label={t("ee.children")} inputMode="numeric" value={num(profile.children)} onChange={(e) => setRoot({ children: toNumber(e.target.value) ?? 0 })} />
          <TriSelect label={t("fsw.form.quebec")} value={profile.intends_quebec} onChange={(v) => setRoot({ intends_quebec: v })} />
        </div>
        <div className="row">
          <SoftToggle label={t("ee.hasSpouse")} checked={Boolean(profile.has_spouse)} onChange={(v) => setRoot({ has_spouse: v })} />
          {profile.has_spouse ? (
            <SoftToggle label={t("ee.spouseAccompanying")} checked={Boolean(profile.spouse_accompanying)} onChange={(v) => setRoot({ spouse_accompanying: v })} />
          ) : null}
        </div>
      </Section>

      <Section title={t("fsw.form.languages")} hint={t("fsw.form.languagesHint")}>
        <LanguageFields label={t("ee.firstLanguage")} value={principal.first_language as Json | undefined} onChange={(v) => setP({ first_language: v })} />
        <LanguageFields label={t("ee.secondLanguage")} value={principal.second_language as Json | undefined} onChange={(v) => setP({ second_language: v })} />
      </Section>

      <Section title={t("fsw.form.education")} hint={t("fsw.form.educationHint")}>
        <div className="form-grid">
          <SelectField
            label={t("ee.education")}
            value={String(principal.education ?? "")}
            options={[{ value: "", label: t("ee.unknown") }, ...EDUCATION.map((x) => ({ value: x, label: t(`ee.educationLevels.${x}`) }))]}
            onChange={(e) => setP({ education: e.target.value || undefined })}
          />
          <TextField label={t("fsw.form.ecaBody")} value={String(eca.body ?? "")} onChange={(e) => setEca({ body: e.target.value })} />
          <TextField label={t("fsw.form.ecaDate")} type="date" value={String(eca.date ?? "")} onChange={(e) => setEca({ date: e.target.value || undefined })} />
          <SelectField
            label={t("ee.canadianEducation")}
            value={String(principal.canadian_education ?? "none")}
            options={["none", "1_2", "3plus"].map((x) => ({ value: x, label: t(`ee.canadianEducationLevels.${x}`) }))}
            onChange={(e) => setP({ canadian_education: e.target.value })}
          />
        </div>
        <SoftToggle label={t("fsw.form.studyCanada")} checked={Boolean(principal.canadian_study_2y)} onChange={(v) => setP({ canadian_study_2y: v })} />
      </Section>

      <Section title={t("fsw.form.work")} hint={t("fsw.form.workHint")}>
        <div className="form-grid">
          <TextField label={t("fsw.form.noc")} inputMode="numeric" maxLength={5} value={String(skilled.noc ?? "")} onChange={(e) => setSkilled({ noc: e.target.value.replace(/\D/g, "") })} />
          <SelectField
            label={t("fsw.form.teer")}
            value={skilled.teer === undefined ? "" : String(skilled.teer)}
            options={TEER.map((x) => ({ value: x, label: x === "" ? t("ee.unknown") : `TEER ${x}` }))}
            onChange={(e) => setSkilled({ teer: e.target.value === "" ? undefined : Number(e.target.value) })}
          />
          <TextField label={t("fsw.form.continuous")} inputMode="decimal" value={num(skilled.continuous_years)} onChange={(e) => setSkilled({ continuous_years: toNumber(e.target.value) })} />
          <TextField label={t("fsw.form.skilledYears")} inputMode="decimal" value={num(skilled.years)} onChange={(e) => setSkilled({ years: toNumber(e.target.value) })} />
          <TriSelect label={t("fsw.form.window")} value={skilled.within_window} onChange={(v) => setSkilled({ within_window: v })} />
          <TextField label={t("fsw.form.recentMonths")} inputMode="numeric" value={num(skilled.recent_months)} onChange={(e) => setSkilled({ recent_months: toNumber(e.target.value) })} />
          <TextField label={t("ee.foreignExperience")} inputMode="numeric" value={num(principal.foreign_experience_years)} onChange={(e) => setP({ foreign_experience_years: toNumber(e.target.value) })} />
          <TextField label={t("ee.canadianExperience")} inputMode="numeric" value={num(principal.canadian_experience_years)} onChange={(e) => setP({ canadian_experience_years: toNumber(e.target.value) })} />
        </div>
        <SoftToggle label={t("ee.certificate")} checked={Boolean(principal.certificate_of_qualification)} onChange={(v) => setP({ certificate_of_qualification: v })} />
        <WorkHistory rows={(principal.work_history as Row[] | undefined) ?? []} onChange={(rows) => setP({ work_history: rows })} />
      </Section>

      <Section title={t("fsw.form.funds")} hint={t("fsw.form.fundsHint")}>
        <div className="form-grid">
          <TextField label={t("fsw.form.fundsCad")} inputMode="numeric" value={num(profile.funds_available_cad)} onChange={(e) => setRoot({ funds_available_cad: toNumber(e.target.value) })} />
          <TriSelect label={t("ee.jobOffer")} value={profile.job_offer} onChange={(v) => setRoot({ job_offer: v })} />
          <TriSelect label={t("fsw.form.authorized")} value={profile.authorized_to_work_canada} onChange={(v) => setRoot({ authorized_to_work_canada: v })} />
        </div>
      </Section>

      {profile.has_spouse ? (
        <Section title={t("ee.spouse")} hint={t("fsw.form.spouseHint")}>
          <div className="row">
            <SoftToggle label={t("fsw.form.spouseCitizen")} checked={Boolean(profile.spouse_is_citizen_pr)} onChange={(v) => setRoot({ spouse_is_citizen_pr: v })} />
          </div>
          <div className="form-grid">
            <TextField label={t("ee.age")} inputMode="numeric" value={num(spouse.age)} onChange={(e) => setS({ age: toNumber(e.target.value) })} />
            <TextField label={t("fsw.form.birth")} type="date" value={String(spouse.date_of_birth ?? "")} onChange={(e) => setS({ date_of_birth: e.target.value || undefined })} />
            <SelectField
              label={t("ee.education")}
              value={String(spouse.education ?? "")}
              options={[{ value: "", label: t("ee.unknown") }, ...EDUCATION.map((x) => ({ value: x, label: t(`ee.educationLevels.${x}`) }))]}
              onChange={(e) => setS({ education: e.target.value || undefined })}
            />
            <TextField label={t("fsw.form.ecaBody")} value={String(((spouse.eca ?? {}) as Json).body ?? "")} onChange={(e) => setS({ eca: { ...((spouse.eca ?? {}) as Json), body: e.target.value } })} />
            <TextField label={t("fsw.form.ecaDate")} type="date" value={String(((spouse.eca ?? {}) as Json).date ?? "")} onChange={(e) => setS({ eca: { ...((spouse.eca ?? {}) as Json), date: e.target.value || undefined } })} />
            <TextField label={t("ee.foreignExperience")} inputMode="numeric" value={num(spouse.foreign_experience_years)} onChange={(e) => setS({ foreign_experience_years: toNumber(e.target.value) })} />
            <TextField label={t("ee.canadianExperience")} inputMode="numeric" value={num(spouse.canadian_experience_years)} onChange={(e) => setS({ canadian_experience_years: toNumber(e.target.value) })} />
            <TextField label={t("fsw.form.noc")} inputMode="numeric" maxLength={5} value={String(spouseSkilled.noc ?? "")} onChange={(e) => setS({ skilled_experience: { ...spouseSkilled, noc: e.target.value.replace(/\D/g, "") } })} />
            <SelectField
              label={t("fsw.form.teer")}
              value={spouseSkilled.teer === undefined ? "" : String(spouseSkilled.teer)}
              options={TEER.map((x) => ({ value: x, label: x === "" ? t("ee.unknown") : `TEER ${x}` }))}
              onChange={(e) => setS({ skilled_experience: { ...spouseSkilled, teer: e.target.value === "" ? undefined : Number(e.target.value) } })}
            />
            <TextField label={t("fsw.form.continuous")} inputMode="decimal" value={num(spouseSkilled.continuous_years)} onChange={(e) => setS({ skilled_experience: { ...spouseSkilled, continuous_years: toNumber(e.target.value) } })} />
            <TextField label={t("fsw.form.skilledYears")} inputMode="decimal" value={num(spouseSkilled.years)} onChange={(e) => setS({ skilled_experience: { ...spouseSkilled, years: toNumber(e.target.value), within_window: true } })} />
            <TriSelect label={t("fsw.form.spouseJobOffer")} value={profile.spouse_job_offer} onChange={(v) => setRoot({ spouse_job_offer: v })} />
            <TriSelect label={t("fsw.form.spouseAuthorized")} value={profile.spouse_authorized_to_work_canada} onChange={(v) => setRoot({ spouse_authorized_to_work_canada: v })} />
          </div>
          <LanguageFields label={t("fsw.form.spouseFirstLanguage")} value={spouse.language as Json | undefined} onChange={(v) => setS({ language: v })} />
          <LanguageFields label={t("ee.secondLanguage")} value={spouse.second_language as Json | undefined} onChange={(v) => setS({ second_language: v })} />
          <SoftToggle label={t("fsw.form.spouseStudyCanada")} checked={Boolean(spouse.canadian_study_2y)} onChange={(v) => setS({ canadian_study_2y: v })} />
          <WorkHistory rows={(spouse.work_history as Row[] | undefined) ?? []} onChange={(rows) => setS({ work_history: rows })} />
        </Section>
      ) : null}

      <Section title={t("fsw.form.additional")}>
        <div className="row">
          <SoftToggle label={t("ee.nomination")} checked={Boolean(additional.provincial_nomination)} onChange={(v) => setA({ provincial_nomination: v })} />
          <SoftToggle label={t("ee.sibling")} checked={Boolean(additional.sibling_in_canada)} onChange={(v) => setA({ sibling_in_canada: v })} />
          <SoftToggle label={t("fsw.form.relative")} checked={Boolean(additional.relative_in_canada)} onChange={(v) => setA({ relative_in_canada: v })} />
        </div>
      </Section>

      <Section title={t("fsw.form.milestones")} hint={t("fsw.form.milestonesHint")}>
        <div className="form-grid">
          <TextField label={t("fsw.form.eeProfileDate")} type="date" value={String(milestones.ee_profile_date ?? "")} onChange={(e) => setM({ ee_profile_date: e.target.value || undefined })} />
          <TextField label={t("fsw.form.itaDate")} type="date" value={String(milestones.ita_date ?? "")} onChange={(e) => setM({ ita_date: e.target.value || undefined })} />
        </div>
      </Section>

      {errors.profile ? (
        <p role="alert" className="ko">
          {errors.profile}
        </p>
      ) : null}
      <div className="row sticky-actions">
        <Button disabled={update.isPending} onClick={save}>
          {t("ee.saveProfile")}
        </Button>
        {update.isSuccess ? (
          <span role="status" className="ok">
            {t("common.saved")}
          </span>
        ) : null}
      </div>
    </GlassCard>
  );
}
